import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useBlocker, useRouter } from '@tanstack/react-router';
import { ArrowRight, Loader2, Pencil, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Textarea } from '@/components/atoms/textarea';
import { FormPageLayout } from '@/components/layouts';
import { ImpactRow } from '@/components/molecules/ImpactRow';
import { SectionCard } from '@/components/molecules/SectionCard';
import { formatSignedCurrency } from '@/components/molecules/financialImpact';
import {
  LineItemPicker,
  type LineItemPickerAddMeta,
  type PickedLineItem,
} from '@/components/organisms/LineItemPicker';
import { toChangeOrderLinePayload } from '@/lib/change-orders/changeOrderLinePayload';
import { lineIdentityKey } from '@/lib/sales/lineIdentity';
import {
  useChangeOrder,
  useChangeOrderImpactPreview,
  useCreateChangeOrder,
  useSendChangeOrder,
  useUpdateChangeOrder,
} from '@/hooks/change-orders/useChangeOrders';
import { useJob } from '@/hooks/jobs/useJob';
import { cn } from '@/lib/utils';
import type {
  ChangeOrder,
  ChangeOrderImpactPreview,
  ChangeOrderLineItem,
  ChangeOrderScope,
  CreateChangeOrderDto,
  UpdateChangeOrderDto,
} from '@/types/changeOrder';

export const Route = createFileRoute('/_dashboard/dashboard/jobs/$id/change-orders/new')({
  component: NewChangeOrderPage,
});

type JobLineItem = Record<string, unknown>;
type ApprovalMethod = 'Signature' | 'Online' | 'Email' | 'Verbal';
type ModifyDraft = {
  quantity: string;
  unitPrice: string;
  name?: string;
  description?: string;
  type?: string;
  priceStrategy?: string;
};

const APPROVAL_METHODS: ApprovalMethod[] = ['Signature', 'Online', 'Email', 'Verbal'];

function approvalMethodToApi(method: ApprovalMethod) {
  return method.toUpperCase() as CreateChangeOrderDto['approvalMethod'];
}

function approvalMethodFromApi(method?: string | null): ApprovalMethod {
  const normalized = String(method ?? '').toUpperCase();
  if (normalized === 'ONLINE') return 'Online';
  if (normalized === 'EMAIL') return 'Email';
  if (normalized === 'VERBAL') return 'Verbal';
  return 'Signature';
}
const CUSTOM_ITEM_TYPES = ['SERVICE', 'MATERIAL', 'EQUIPMENT'] as const;

function asJobLineItem(item: ChangeOrderLineItem): JobLineItem {
  return item as unknown as JobLineItem;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  const normalized =
    typeof value === 'string'
      ? value.trim().includes(',') && value.trim().includes('.')
        ? value.trim().replace(/,/g, '')
        : value.trim().replace(',', '.')
      : value;
  const n = Number(normalized ?? fallback);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeDecimalInput(value: string): string {
  const trimmed = value.trim();
  return trimmed.includes(',') && trimmed.includes('.')
    ? trimmed.replace(/,/g, '')
    : trimmed.replace(',', '.');
}

function formatMoneyInput(value: unknown): string {
  return asNumber(value, 0).toFixed(2);
}

function formatQuantityInput(value: unknown): string {
  const number = asNumber(value, 0);
  return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(2)));
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatChangeOrderNumber(changeOrderNumber: number): string {
  return `CO-${String(changeOrderNumber).padStart(4, '0')}`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function resolveJobHeaderName(job: {
  jobNumber?: string;
  name?: string;
  jobType?: string | { name?: string };
}) {
  const name = asString(job.name);
  const jobNumber = asString(job.jobNumber);
  const jobTypeName = typeof job.jobType === 'string' ? job.jobType : job.jobType?.name;
  const generatedName = jobNumber ? `Job ${jobNumber}` : '';

  if (!name || name === jobNumber || name.toLowerCase() === generatedName.toLowerCase()) {
    return asString(jobTypeName, 'Job');
  }

  if (jobNumber) {
    const nameWithoutGeneratedPrefix = name
      .replace(new RegExp(`^job\\s+${escapeRegExp(jobNumber)}\\s*(?:[-:]\\s*)?`, 'i'), '')
      .trim();

    if (nameWithoutGeneratedPrefix) return nameWithoutGeneratedPrefix;
  }

  return name;
}

/**
 * The id the SERVER knows this line by.
 *
 * This is what may be sent in a payload: the backend matches removals and
 * modifications against the job's own line ids and has never heard of a
 * configuration-qualified key.
 */
function persistedLineItemId(item: JobLineItem, index: number): string {
  return (
    asString(item.lineItemId) ||
    asString(item.itemId) ||
    asString(item.snapshotId) ||
    asString(item.id) ||
    `item-${index}`
  );
}

/**
 * The key this line's LOCAL row state is held under — open/removed/draft sets.
 *
 * A configured line qualifies its key so two configurations of the same
 * configurable item don't collapse into one row. Unconfigured lines keep the
 * bare id, so keys already persisted on saved change orders still match.
 *
 * Deliberately NOT the same value as {@link persistedLineItemId}: this one must
 * never reach the server.
 */
function lineItemId(item: JobLineItem, index: number): string {
  const base = persistedLineItemId(item, index);
  const configurationName = asString(item.configurationName);
  return configurationName ? `${base}::${configurationName}` : base;
}

function lineItemName(item: JobLineItem): string {
  return asString(item.name) || asString(item.description, 'Item');
}

function lineItemQuantity(item: JobLineItem): number {
  return asNumber(item.quantity ?? item.qty, 1);
}

function isServiceType(value: unknown): boolean {
  return asString(value).toUpperCase() === 'SERVICE';
}

function isTimeAndMaterialStrategy(value: unknown): boolean {
  const strategy = asString(value).toUpperCase();
  return (
    strategy.includes('TIME') ||
    strategy.includes('T&M') ||
    strategy.includes('T_AND_M') ||
    strategy.includes('TIME_AND_MATERIAL')
  );
}

function isTimeAndMaterialItem(item: JobLineItem): boolean {
  return (
    isServiceType(item.type) &&
    isTimeAndMaterialStrategy(item.priceStrategy ?? item.pricingStrategy)
  );
}

function draftBillingMode(draft: ModifyDraft, item: JobLineItem): 'FLAT' | 'T&M' {
  const type = draft.type ?? item.type;
  if (!isServiceType(type)) return 'FLAT';
  return isTimeAndMaterialStrategy(
    draft.priceStrategy ?? item.priceStrategy ?? item.pricingStrategy,
  )
    ? 'T&M'
    : 'FLAT';
}

function lineItemUnitPrice(item: JobLineItem): number {
  if (isTimeAndMaterialItem(item)) {
    return asNumber(
      item.manualPriceOverride ?? item.laborRate ?? item.labor_rate ?? item.basePrice ?? item.price,
      0,
    );
  }
  return asNumber(item.manualPriceOverride ?? item.unitPrice ?? item.price ?? item.basePrice, 0);
}

/**
 * A line's amount before the job's discount and tax — quantity x unit price —
 * which is what every amount in the scope list and the Financial impact's
 * added / removed / modified figures is measured in. A job line's `lineTotal`
 * is after its share of the discount and its tax, and comparing that with a
 * typed quantity x price made an untouched line look changed (NOT-784).
 */
function lineItemSubtotal(item: JobLineItem): number {
  const fallback = lineItemQuantity(item) * lineItemUnitPrice(item);
  return asNumber(item.lineSubtotal, fallback);
}

function calculateLineTotal(quantity: number, unitPrice: number): number {
  return Math.round(quantity * unitPrice * 100) / 100;
}

function formatItemQuantity(item: JobLineItem, quantity: number): string {
  return isTimeAndMaterialItem(item) ? `${formatNumber(quantity)} h` : `x${formatNumber(quantity)}`;
}

function formatQuantityForBilling(billingMode: 'FLAT' | 'T&M', quantity: number): string {
  return billingMode === 'T&M' ? `${formatNumber(quantity)} h` : `x${formatNumber(quantity)}`;
}

function toChangeOrderLineItem(
  item: PickedLineItem,
  source: 'CHANGE_ORDER' | 'CUSTOM_CHANGE_ORDER' = 'CHANGE_ORDER',
): ChangeOrderLineItem {
  const priceStrategy = item.priceStrategy ?? 'FLAT';
  const unitPrice = isTimeAndMaterialStrategy(priceStrategy)
    ? item.laborRate || item.basePrice
    : item.basePrice;
  const lineSubtotal = calculateLineTotal(item.quantity, unitPrice);
  return {
    lineItemId: item.lineItemId,
    itemVersion: item.itemVersion,
    name: item.name,
    code: item.code || `CO-${Date.now()}`,
    type: item.type,
    unit: item.unit || 'each',
    description: item.description ?? '',
    basePrice: unitPrice,
    laborRate: isTimeAndMaterialStrategy(priceStrategy) ? unitPrice : (item.laborRate ?? 0),
    costBasis: item.costBasis ?? 0,
    markupPercent: item.markupPercent ?? 0,
    currency: 'USD',
    priceStrategy,
    taxable: item.taxable ?? false,
    taxCategory: item.taxLineType ?? undefined,
    taxCode: undefined,
    quantity: item.quantity,
    discountAmount: 0,
    discountPercent: 0,
    lineSubtotal,
    lineTax: 0,
    lineTotal: lineSubtotal,
    source,
    ...(item.configurationName && { configurationName: item.configurationName }),
  };
}

function applyLineItemDraft(item: ChangeOrderLineItem, draft: ModifyDraft): ChangeOrderLineItem {
  const quantity = asNumber(draft.quantity, lineItemQuantity(asJobLineItem(item)));
  const unitPrice = asNumber(draft.unitPrice, lineItemUnitPrice(asJobLineItem(item)));
  const lineSubtotal = calculateLineTotal(quantity, unitPrice);
  const type = draft.type || item.type;
  const priceStrategy = isServiceType(type)
    ? (draft.priceStrategy ?? item.priceStrategy ?? 'FLAT')
    : 'FLAT';
  const timeAndMaterial = isTimeAndMaterialStrategy(priceStrategy);

  return {
    ...item,
    name: draft.name?.trim() || item.name,
    description: draft.description ?? item.description,
    type,
    priceStrategy,
    quantity,
    basePrice: unitPrice,
    laborRate: timeAndMaterial ? unitPrice : item.laborRate,
    manualPriceOverride: unitPrice,
    lineSubtotal,
    lineTotal: lineSubtotal,
  };
}

function modificationDraftFor(item: JobLineItem): ModifyDraft {
  return {
    name: lineItemName(item),
    description: asString(item.description),
    type: asString(item.type, 'SERVICE'),
    priceStrategy: isTimeAndMaterialItem(item) ? 'T&M' : 'FLAT',
    quantity: formatQuantityInput(lineItemQuantity(item)),
    unitPrice: formatMoneyInput(lineItemUnitPrice(item)),
  };
}

/**
 * Persisted id → local row key, for reading a SAVED change order back in.
 *
 * A saved draft stores the ids the server knows. The editor keys its state by
 * the local row key, which for a configured line is qualified. Without this
 * translation, reopening a draft that removed or modified such a line finds no
 * matching row: the change vanishes from the editor and is dropped on the next
 * save.
 */
function localRowKeysByPersistedId(currentItems: JobLineItem[]): Map<string, string> {
  return new Map(
    currentItems.map(
      (item, index) => [persistedLineItemId(item, index), lineItemId(item, index)] as const,
    ),
  );
}

function modificationDraftsFromChangeOrder(
  changeOrder: ChangeOrder,
  currentItems: JobLineItem[],
): Record<string, ModifyDraft> {
  const currentItemMap = new Map(
    currentItems.map((item, index) => [lineItemId(item, index), item] as const),
  );
  const rowKeys = localRowKeysByPersistedId(currentItems);

  return changeOrder.lineItemsModified.reduce<Record<string, ModifyDraft>>((drafts, item) => {
    // The saved draft carries the SERVER's id; everything here is keyed by the
    // local row key. Fall back to the raw id for a line no longer on the job,
    // which is the same thing the old code did for a missing match.
    const rowKey = rowKeys.get(item.lineItemId) ?? item.lineItemId;
    const currentItem = currentItemMap.get(rowKey);
    const baseDraft = currentItem
      ? modificationDraftFor(currentItem)
      : modificationDraftFor(item.updated as JobLineItem);
    const updated = item.updated as Record<string, unknown>;
    const nextPriceStrategy = asString(updated.priceStrategy, baseDraft.priceStrategy ?? 'FLAT');
    const nextUnitPrice =
      updated.manualPriceOverride ?? updated.basePrice ?? updated.laborRate ?? baseDraft.unitPrice;

    drafts[rowKey] = {
      ...baseDraft,
      quantity: formatQuantityInput(updated.quantity ?? baseDraft.quantity),
      unitPrice: formatMoneyInput(nextUnitPrice),
      priceStrategy: isTimeAndMaterialStrategy(nextPriceStrategy) ? 'T&M' : 'FLAT',
    };
    return drafts;
  }, {});
}

function isCustomChangeOrderItem(item: ChangeOrderLineItem): boolean {
  const source = asString(item.source).toUpperCase();
  return (
    !source ||
    source.includes('CUSTOM') ||
    source.includes('MANUAL') ||
    (source === 'CHANGE_ORDER' && !item.code)
  );
}

function serializeFormState({
  reason,
  approvalMethod,
  addedItems,
  removedIds,
  appliedModifyDrafts,
}: {
  reason: string;
  approvalMethod: ApprovalMethod;
  addedItems: ChangeOrderLineItem[];
  removedIds: Set<string>;
  appliedModifyDrafts: Record<string, ModifyDraft>;
}): string {
  return JSON.stringify({
    reason,
    approvalMethod,
    addedItems,
    removedIds: [...removedIds].sort(),
    appliedModifyDrafts,
  });
}

function ChangeTag({
  children,
  tone,
}: {
  children: string;
  tone: 'success' | 'warning' | 'danger';
}) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-semibold',
        tone === 'success' && 'bg-emerald-100 text-emerald-700',
        tone === 'warning' && 'bg-amber-100 text-amber-700',
        tone === 'danger' && 'bg-destructive/10 text-destructive',
      )}
    >
      {children}
    </span>
  );
}

function FieldChangeLine({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="font-medium text-muted-foreground">{label}:</span>
      <span className="tabular-nums text-muted-foreground line-through">{before}</span>
      <ArrowRight className="size-3.5 text-muted-foreground" />
      <span className="font-semibold tabular-nums text-foreground">{after}</span>
    </div>
  );
}

function LineItemInlineEditor({
  item,
  draft,
  onChange,
  onCancel,
  onApply,
  showItemFields,
  hideChangeSummary,
  billingEditable = true,
}: {
  item: JobLineItem;
  draft: ModifyDraft;
  onChange: (patch: Partial<ModifyDraft>) => void;
  onCancel: () => void;
  onApply: () => void;
  showItemFields?: boolean;
  hideChangeSummary?: boolean;
  billingEditable?: boolean;
}) {
  const type = asString(draft.type ?? item.type, 'SERVICE').toUpperCase();
  const isService = isServiceType(type);
  const billingMode = draftBillingMode(draft, item);
  const quantityLabel = billingMode === 'T&M' ? 'Hours' : 'Qty';
  const priceLabel = billingMode === 'T&M' ? 'Rate/hr' : 'Unit price';
  const originalQuantity = lineItemQuantity(item);
  const originalUnitPrice = lineItemUnitPrice(item);
  const originalTotal = lineItemSubtotal(item);
  const nextQuantity = asNumber(draft.quantity, lineItemQuantity(item));
  const nextUnitPrice = asNumber(draft.unitPrice, lineItemUnitPrice(item));
  const quantityChanged = nextQuantity !== originalQuantity;
  const priceChanged = nextUnitPrice !== originalUnitPrice;
  const nextTotal = calculateLineTotal(nextQuantity, nextUnitPrice);
  const draftDelta = nextTotal - originalTotal;
  const editorGridClass = hideChangeSummary
    ? isService
      ? 'sm:grid-cols-[160px_120px_160px]'
      : 'sm:grid-cols-[120px_160px]'
    : isService
      ? 'sm:grid-cols-[160px_120px_160px_minmax(0,1fr)]'
      : 'sm:grid-cols-[120px_160px_minmax(0,1fr)]';

  function handleTypeChange(nextType: string) {
    onChange({
      type: nextType,
      priceStrategy: nextType === 'SERVICE' ? (draft.priceStrategy ?? 'FLAT') : 'FLAT',
    });
  }

  function handleBillingChange(nextMode: 'FLAT' | 'T&M') {
    onChange({
      priceStrategy: nextMode,
      quantity: nextMode === 'T&M' ? draft.quantity || '0' : draft.quantity || '1',
      unitPrice: formatMoneyInput(draft.unitPrice),
    });
  }

  return (
    <div className="mt-3 rounded-md bg-muted/60 p-3">
      {showItemFields ? (
        <div className="mb-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_160px_minmax(0,1fr)]">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input
              value={draft.name ?? ''}
              onChange={(event) => onChange({ name: event.target.value })}
              placeholder="Item name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Type</Label>
            <Select value={draft.type ?? 'SERVICE'} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CUSTOM_ITEM_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.charAt(0) + type.slice(1).toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Description</Label>
            <Input
              value={draft.description ?? ''}
              onChange={(event) => onChange({ description: event.target.value })}
              placeholder="Optional description"
            />
          </div>
        </div>
      ) : null}
      <div className={cn('grid gap-3', editorGridClass)}>
        {isService ? (
          <div className="space-y-1.5">
            <Label className="text-xs">Billing</Label>
            {billingEditable ? (
              <div className="inline-flex rounded-md border bg-background p-0.5">
                {(['FLAT', 'T&M'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={cn(
                      'h-8 rounded px-3 text-sm font-medium text-muted-foreground transition-colors',
                      billingMode === mode && 'bg-foreground text-background',
                    )}
                    onClick={() => handleBillingChange(mode)}
                  >
                    {mode === 'FLAT' ? 'Flat' : 'T&M'}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex h-9 items-center rounded-md border bg-background px-3 text-sm font-medium">
                {billingMode === 'T&M' ? 'T&M' : 'Flat'}
              </div>
            )}
          </div>
        ) : null}
        <div className="space-y-1.5">
          <Label className="text-xs">{quantityLabel}</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={draft.quantity}
            onBlur={() => onChange({ quantity: formatQuantityInput(draft.quantity) })}
            onChange={(event) => onChange({ quantity: normalizeDecimalInput(event.target.value) })}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{priceLabel}</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={draft.unitPrice}
            onBlur={() => onChange({ unitPrice: formatMoneyInput(draft.unitPrice) })}
            onChange={(event) => onChange({ unitPrice: normalizeDecimalInput(event.target.value) })}
          />
        </div>
        {!hideChangeSummary ? (
          <div className="space-y-2 text-sm">
            {quantityChanged ? (
              <FieldChangeLine
                label={quantityLabel}
                before={formatNumber(originalQuantity)}
                after={formatNumber(nextQuantity)}
              />
            ) : null}
            {priceChanged ? (
              <FieldChangeLine
                label={priceLabel}
                before={formatCurrency(originalUnitPrice)}
                after={formatCurrency(nextUnitPrice)}
              />
            ) : null}
            {!quantityChanged && !priceChanged ? (
              <p className="text-muted-foreground">No field changes yet.</p>
            ) : null}
            <div className="flex items-center gap-2 font-medium">
              <span className="text-muted-foreground">Item delta</span>
              <span
                className={cn(
                  'tabular-nums',
                  draftDelta > 0 && 'text-emerald-700',
                  draftDelta < 0 && 'text-destructive',
                  draftDelta === 0 && 'text-muted-foreground',
                )}
              >
                {draftDelta === 0 ? '—' : formatSignedCurrency(draftDelta)}
              </span>
            </div>
          </div>
        ) : null}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        {hideChangeSummary ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Item total</span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatCurrency(nextTotal)}
            </span>
          </div>
        ) : (
          <div className="flex items-center text-sm text-muted-foreground">
            <span className="tabular-nums">{formatCurrency(originalTotal)}</span>
            <ArrowRight className="mx-2 size-4" />
            <span className="font-semibold tabular-nums text-foreground">
              {formatCurrency(nextTotal)}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={onApply}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The Financial impact panel. Every figure is the server's price of the scope
 * the editor holds — the numbers the change order records when it is saved —
 * and each adds up to the next: the line amounts, any change in the job's
 * discounts, the tax on it, then the net impact the Billing tab shows.
 */
function ChangeOrderImpactSummary({
  impact,
  currentTotal,
  pending,
  failed,
}: {
  /** Absent while there is nothing to price, or before the first answer. */
  impact: ChangeOrderImpactPreview | undefined;
  currentTotal: number;
  pending: boolean;
  failed: boolean;
}) {
  const placeholder = failed ? '—' : pending && !impact ? '…' : undefined;
  const value = (pick: (figures: ChangeOrderImpactPreview) => number) =>
    impact ? pick(impact) : 0;
  const net = value((figures) => figures.totalDelta);

  return (
    <div className={cn('mt-5 space-y-4 text-sm', pending && impact && 'opacity-60')}>
      <ImpactRow
        label="Added"
        value={value((figures) => figures.addedSubtotal)}
        tone="success"
        placeholder={placeholder}
      />
      <ImpactRow
        label="Removed"
        value={-value((figures) => figures.removedSubtotal)}
        tone="danger"
        placeholder={placeholder}
      />
      <ImpactRow
        label="Modified"
        value={value((figures) => figures.modifiedSubtotalDelta)}
        tone="neutral"
        placeholder={placeholder}
      />
      {impact && impact.discountDelta !== 0 ? (
        <ImpactRow
          label="Discount"
          value={-impact.discountDelta}
          tone="neutral"
          placeholder={placeholder}
        />
      ) : null}
      <ImpactRow
        label="Tax"
        value={value((figures) => figures.taxDelta)}
        tone="neutral"
        placeholder={placeholder}
      />
      <div className="border-t pt-4">
        <ImpactRow label="Net impact" value={net} prominent placeholder={placeholder} />
      </div>
      <ImpactRow
        label="New job total"
        value={impact ? impact.newTotal : currentTotal}
        tone={net > 0 ? 'success' : net < 0 ? 'danger' : 'neutral'}
        total
        placeholder={placeholder}
      />
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {failed
          ? 'Tax and totals could not be calculated. The change order is priced when you save it.'
          : pending
            ? 'Calculating tax and totals…'
            : 'Item amounts are before tax. Net impact and the new job total include tax.'}
      </p>
    </div>
  );
}

function NewChangeOrderPage() {
  const { id } = Route.useParams();
  return <ChangeOrderFormPage jobId={id} mode="create" />;
}

export function ChangeOrderFormPage({
  jobId,
  mode,
  changeOrderId,
}: {
  jobId: string;
  mode: 'create' | 'edit';
  changeOrderId?: string;
}) {
  const router = useRouter();
  const allowNextNavigationRef = useRef(false);
  const initializedChangeOrderIdRef = useRef<string | null>(null);
  const initialSnapshotRef = useRef<string>('');
  const { data: job, isLoading: isJobLoading } = useJob(jobId);
  const { data: changeOrder, isLoading: isChangeOrderLoading } = useChangeOrder(
    changeOrderId ?? '',
  );
  const create = useCreateChangeOrder();
  const update = useUpdateChangeOrder();
  const send = useSendChangeOrder();

  const [reason, setReason] = useState('');
  const [approvalMethod, setApprovalMethod] = useState<ApprovalMethod>('Signature');
  const [addedItems, setAddedItems] = useState<ChangeOrderLineItem[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());
  const [modifyOpenIds, setModifyOpenIds] = useState<Set<string>>(() => new Set());
  const [modifyDrafts, setModifyDrafts] = useState<Record<string, ModifyDraft>>({});
  const [appliedModifyDrafts, setAppliedModifyDrafts] = useState<Record<string, ModifyDraft>>({});
  const [addedOpenIds, setAddedOpenIds] = useState<Set<string>>(() => new Set());
  const [addedDrafts, setAddedDrafts] = useState<Record<string, ModifyDraft>>({});
  const [customAddedIds, setCustomAddedIds] = useState<Set<string>>(() => new Set());
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const jobFinancialsRoute = {
    to: '/dashboard/jobs/$id',
    params: { id: jobId },
    search: { tab: 'billing' as const },
  };
  const navigateBack = () => void router.navigate(jobFinancialsRoute);

  const currentItems = useMemo(() => (job?.lineItems ?? []) as JobLineItem[], [job?.lineItems]);
  const isEditable = mode === 'create' || changeOrder?.status === 'DRAFT';

  useEffect(() => {
    if (mode !== 'edit' || !changeOrder || !job) return;
    if (initializedChangeOrderIdRef.current === changeOrder.id) return;

    const nextReason = changeOrder.reason || changeOrder.description || '';
    const nextAddedItems = changeOrder.lineItemsAdded;
    // Same translation as the modification drafts: stored server ids, local
    // row keys in state.
    const rowKeys = localRowKeysByPersistedId(currentItems);
    const nextRemovedIds = new Set(changeOrder.lineItemsRemoved.map((id) => rowKeys.get(id) ?? id));
    const nextAppliedModifyDrafts = modificationDraftsFromChangeOrder(changeOrder, currentItems);
    const nextCustomAddedIds = new Set(
      nextAddedItems.filter(isCustomChangeOrderItem).map((lineItem) => lineItem.lineItemId),
    );

    setReason(nextReason);
    const nextApprovalMethod = approvalMethodFromApi(changeOrder.approvalMethod);

    setApprovalMethod(nextApprovalMethod);
    setAddedItems(nextAddedItems);
    setRemovedIds(nextRemovedIds);
    setModifyOpenIds(new Set());
    setModifyDrafts({});
    setAppliedModifyDrafts(nextAppliedModifyDrafts);
    setAddedOpenIds(new Set());
    setAddedDrafts({});
    setCustomAddedIds(nextCustomAddedIds);

    initialSnapshotRef.current = serializeFormState({
      reason: nextReason,
      approvalMethod: nextApprovalMethod,
      addedItems: nextAddedItems,
      removedIds: nextRemovedIds,
      appliedModifyDrafts: nextAppliedModifyDrafts,
    });
    initializedChangeOrderIdRef.current = changeOrder.id;
  }, [changeOrder, currentItems, job, mode]);

  const currentLineItemsTotal = currentItems.reduce(
    (sum, item) => sum + asNumber(item.lineTotal, lineItemSubtotal(item)),
    0,
  );
  const currentTotal =
    job?.grandTotal != null && job.grandTotal > 0 ? job.grandTotal : currentLineItemsTotal;

  const modifiedItems = useMemo(() => {
    return currentItems.flatMap((item, index) => {
      const itemId = lineItemId(item, index);
      if (removedIds.has(itemId)) return [];
      const draft = appliedModifyDrafts[itemId];
      if (!draft) return [];

      const nextQuantity = asNumber(draft.quantity, lineItemQuantity(item));
      const nextUnitPrice = asNumber(draft.unitPrice, lineItemUnitPrice(item));
      const nextPriceStrategy = draftBillingMode(draft, item);
      const quantityChanged = nextQuantity !== lineItemQuantity(item);
      const priceChanged = nextUnitPrice !== lineItemUnitPrice(item);
      const billingChanged = nextPriceStrategy !== (isTimeAndMaterialItem(item) ? 'T&M' : 'FLAT');
      if (!quantityChanged && !priceChanged && !billingChanged) return [];

      return [
        {
          item,
          itemId,
          // What the payload sends; `itemId` is the local row key.
          persistedId: persistedLineItemId(item, index),
          nextQuantity,
          nextUnitPrice,
          nextPriceStrategy,
          newTotal: calculateLineTotal(nextQuantity, nextUnitPrice),
        },
      ];
    });
  }, [appliedModifyDrafts, currentItems, removedIds]);

  const hasReason = Boolean(reason.trim());
  const hasChanges = addedItems.length > 0 || removedIds.size > 0 || modifiedItems.length > 0;
  // The scope exactly as a save sends it. The Financial impact is this, priced
  // by the server, so the editor shows the numbers the change order records.
  const scope = useMemo<ChangeOrderScope>(
    () => ({
      // A reopened draft's lines are the stored ones, server stamps included.
      lineItemsAdded: addedItems.map(toChangeOrderLinePayload),
      // Row keys are LOCAL. Translate back to the ids the server knows, or a
      // configured line's removal/modification silently matches nothing and the
      // change order applies without it.
      // Index is captured BEFORE filtering: it is the last-resort fallback in
      // both id helpers, and a post-filter index would invent a different one.
      lineItemsRemoved: currentItems
        .map((item, index) => ({ item, index }))
        .filter(({ item, index }) => removedIds.has(lineItemId(item, index)))
        .map(({ item, index }) => persistedLineItemId(item, index)),
      lineItemsModified: modifiedItems.map((item) => ({
        lineItemId: item.persistedId,
        updated: {
          quantity: item.nextQuantity,
          basePrice: item.nextUnitPrice,
          priceStrategy: item.nextPriceStrategy,
          laborRate: item.nextPriceStrategy === 'T&M' ? item.nextUnitPrice : undefined,
          manualPriceOverride: item.nextUnitPrice,
        },
      })),
    }),
    [addedItems, currentItems, modifiedItems, removedIds],
  );
  const impactPreview = useChangeOrderImpactPreview(jobId, scope, hasChanges);
  const impact = hasChanges ? impactPreview.data : undefined;
  const impactPending = hasChanges && (!impact || impactPreview.isFetching);
  const impactFailed = hasChanges && impactPreview.isError && !impactPreview.isFetching;
  const hasOpenModifyChanges = currentItems.some((item, index) => {
    const itemId = lineItemId(item, index);
    if (!modifyOpenIds.has(itemId)) return false;

    const draft = modifyDrafts[itemId];
    if (!draft) return false;

    return (
      asNumber(draft.quantity, lineItemQuantity(item)) !== lineItemQuantity(item) ||
      asNumber(draft.unitPrice, lineItemUnitPrice(item)) !== lineItemUnitPrice(item) ||
      draftBillingMode(draft, item) !== (isTimeAndMaterialItem(item) ? 'T&M' : 'FLAT')
    );
  });
  const currentSnapshot = serializeFormState({
    reason,
    approvalMethod,
    addedItems,
    removedIds,
    appliedModifyDrafts,
  });
  const isDirty =
    mode === 'create'
      ? hasReason || hasChanges
      : Boolean(initialSnapshotRef.current) && currentSnapshot !== initialSnapshotRef.current;
  const hasUnsavedWork = (isDirty || hasOpenModifyChanges) && isEditable;
  const isSaving = create.isPending || update.isPending || send.isPending;
  const canSaveDraft =
    isEditable && (mode === 'edit' ? isDirty : hasReason || hasChanges) && !isSaving;
  const canSendForApproval = isEditable && hasReason && hasChanges && !isSaving;
  const excludeIds = [
    ...currentItems.map((item, index) => lineItemId(item, index)),
    ...addedItems.map((item) => item.lineItemId),
  ];
  const headerSubtitle = job
    ? [job.jobNumber, resolveJobHeaderName(job), `current total ${formatCurrency(currentTotal)}`]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  const blocker = useBlocker({
    disabled: !hasUnsavedWork,
    enableBeforeUnload: false,
    withResolver: true,
    shouldBlockFn: () => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        return false;
      }

      return true;
    },
  });

  function leaveWithoutPrompt() {
    allowNextNavigationRef.current = true;
    navigateBack();
  }

  function handleExit() {
    if (hasUnsavedWork) {
      setExitConfirmOpen(true);
      return;
    }

    leaveWithoutPrompt();
  }

  function closeExitConfirm() {
    setExitConfirmOpen(false);
    if (blocker.status === 'blocked') {
      blocker.reset();
    }
  }

  function confirmExit() {
    setExitConfirmOpen(false);
    if (blocker.status === 'blocked') {
      blocker.proceed();
      return;
    }

    leaveWithoutPrompt();
  }

  function addPickedItem(item: PickedLineItem, meta?: LineItemPickerAddMeta) {
    const changeOrderItem = toChangeOrderLineItem(
      item,
      meta?.source === 'custom' ? 'CUSTOM_CHANGE_ORDER' : 'CHANGE_ORDER',
    );
    setAddedItems((current) => [...current, changeOrderItem]);

    if (meta?.source === 'custom') {
      const rowKey = lineIdentityKey(changeOrderItem);
      setCustomAddedIds((current) => new Set(current).add(rowKey));
      setAddedOpenIds((current) => new Set(current).add(rowKey));
      setAddedDrafts((current) => ({
        ...current,
        [rowKey]: modificationDraftFor(asJobLineItem(changeOrderItem)),
      }));
    }
  }

  function removeAddedItem(rowKeyToRemove: string) {
    setAddedItems((current) => current.filter((item) => lineIdentityKey(item) !== rowKeyToRemove));
    setAddedOpenIds((current) => {
      const next = new Set(current);
      next.delete(rowKeyToRemove);
      return next;
    });
    setAddedDrafts((current) => {
      const next = { ...current };
      delete next[rowKeyToRemove];
      return next;
    });
    setCustomAddedIds((current) => {
      const next = new Set(current);
      next.delete(rowKeyToRemove);
      return next;
    });
  }

  function toggleAddedEdit(item: ChangeOrderLineItem) {
    const rowKey = lineIdentityKey(item);
    setAddedOpenIds((current) => {
      const next = new Set(current);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
        setAddedDrafts((drafts) => ({
          ...drafts,
          [rowKey]: drafts[rowKey] ?? modificationDraftFor(asJobLineItem(item)),
        }));
      }
      return next;
    });
  }

  function updateAddedDraft(itemId: string, patch: Partial<ModifyDraft>) {
    setAddedDrafts((current) => ({
      ...current,
      [itemId]: { ...current[itemId], ...patch } as ModifyDraft,
    }));
  }

  function cancelAddedEdit(itemId: string) {
    setAddedOpenIds((current) => {
      const next = new Set(current);
      next.delete(itemId);
      return next;
    });
    setAddedDrafts((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
  }

  function applyAddedDraft(item: ChangeOrderLineItem) {
    const rowKey = lineIdentityKey(item);
    const draft = addedDrafts[rowKey] ?? modificationDraftFor(asJobLineItem(item));
    const nextQuantity = asNumber(draft.quantity, Number.NaN);
    const nextUnitPrice = asNumber(draft.unitPrice, Number.NaN);

    if (!draft.name?.trim()) {
      toast.error('Item name is required.');
      return;
    }
    if (!Number.isFinite(nextQuantity) || nextQuantity < 0) {
      toast.error('Quantity must be 0 or greater.');
      return;
    }
    if (!Number.isFinite(nextUnitPrice) || nextUnitPrice < 0) {
      toast.error('Unit price must be 0 or greater.');
      return;
    }

    setAddedItems((current) =>
      current.map((currentItem) =>
        lineIdentityKey(currentItem) === rowKey
          ? applyLineItemDraft(currentItem, draft)
          : currentItem,
      ),
    );
    setAddedOpenIds((current) => {
      const next = new Set(current);
      next.delete(rowKey);
      return next;
    });
    setAddedDrafts((current) => {
      const next = { ...current };
      delete next[rowKey];
      return next;
    });
  }

  function toggleRemove(itemId: string) {
    setRemovedIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
        setModifyOpenIds((openIds) => {
          const nextOpen = new Set(openIds);
          nextOpen.delete(itemId);
          return nextOpen;
        });
        setModifyDrafts((drafts) => {
          const nextDrafts = { ...drafts };
          delete nextDrafts[itemId];
          return nextDrafts;
        });
        setAppliedModifyDrafts((drafts) => {
          const nextDrafts = { ...drafts };
          delete nextDrafts[itemId];
          return nextDrafts;
        });
      }
      return next;
    });
  }

  function toggleModify(item: JobLineItem, itemId: string) {
    if (removedIds.has(itemId)) return;
    setModifyOpenIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
        setModifyDrafts((drafts) => ({
          ...drafts,
          [itemId]: appliedModifyDrafts[itemId] ?? modificationDraftFor(item),
        }));
      }
      return next;
    });
  }

  function updateModifyDraft(itemId: string, patch: Partial<ModifyDraft>) {
    setModifyDrafts((current) => ({
      ...current,
      [itemId]: { ...current[itemId], ...patch } as ModifyDraft,
    }));
  }

  function applyModifyDraft(item: JobLineItem, itemId: string) {
    const draft = modifyDrafts[itemId] ?? modificationDraftFor(item);
    const nextQuantity = asNumber(draft.quantity, Number.NaN);
    const nextUnitPrice = asNumber(draft.unitPrice, Number.NaN);
    const nextPriceStrategy = draftBillingMode(draft, item);
    if (!Number.isFinite(nextQuantity) || nextQuantity < 0) {
      toast.error('Quantity must be 0 or greater.');
      return;
    }
    if (!Number.isFinite(nextUnitPrice) || nextUnitPrice < 0) {
      toast.error('Unit price must be 0 or greater.');
      return;
    }

    const original = modificationDraftFor(item);
    const changed =
      nextQuantity !== asNumber(original.quantity) ||
      nextUnitPrice !== asNumber(original.unitPrice) ||
      nextPriceStrategy !== (original.priceStrategy ?? 'FLAT');

    setAppliedModifyDrafts((current) => {
      const next = { ...current };
      if (changed) {
        next[itemId] = {
          quantity: formatQuantityInput(nextQuantity),
          unitPrice: formatMoneyInput(nextUnitPrice),
          priceStrategy: nextPriceStrategy,
        };
      } else {
        delete next[itemId];
      }
      return next;
    });
    setModifyOpenIds((current) => {
      const next = new Set(current);
      next.delete(itemId);
      return next;
    });
  }

  function cancelModify(itemId: string) {
    setModifyOpenIds((current) => {
      const next = new Set(current);
      next.delete(itemId);
      return next;
    });
    setModifyDrafts((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
    setAppliedModifyDrafts((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
  }

  function buildPayload(): CreateChangeOrderDto {
    const trimmedReason = reason.trim();
    return {
      jobId,
      description: trimmedReason || 'Draft change order',
      reason: trimmedReason || undefined,
      approvalMethod: approvalMethodToApi(approvalMethod),
      ...scope,
    };
  }

  async function save(mode: 'draft' | 'send') {
    if (!isEditable) {
      return void toast.error('Only draft change orders can be edited.');
    }
    if (mode === 'draft' && !hasReason && !hasChanges) {
      return void toast.error('Add a reason or a scope change to save a draft.');
    }
    if (mode === 'send' && !hasChanges) {
      return void toast.error('Add, remove, or modify at least one item.');
    }
    if (mode === 'send' && !hasReason) return void toast.error('Reason is required.');

    try {
      if (changeOrderId) {
        const payload = buildPayload();
        const saved = await update.mutateAsync({
          ...(payload as UpdateChangeOrderDto),
          id: changeOrderId,
          jobId,
        });
        if (mode === 'send') {
          await send.mutateAsync({ id: saved.id, channels: ['email', 'sms'] });
        } else {
          toast.success(
            `Change order ${formatChangeOrderNumber(saved.changeOrderNumber)} saved as draft`,
          );
        }
      } else {
        const changeOrder = await create.mutateAsync(buildPayload());
        if (mode === 'send') {
          await send.mutateAsync({ id: changeOrder.id, channels: ['email', 'sms'] });
        } else {
          toast.success(
            `Change order ${formatChangeOrderNumber(changeOrder.changeOrderNumber)} created as draft`,
          );
        }
      }
      leaveWithoutPrompt();
    } catch {
      return;
    }
  }

  const pageTitle =
    mode === 'edit'
      ? isEditable
        ? 'Edit change order'
        : 'Change order details'
      : 'New change order';
  const isLoading = isJobLoading || (mode === 'edit' && isChangeOrderLoading);

  if (isLoading || !job) {
    return (
      <FormPageLayout title={pageTitle} onBack={navigateBack} noscroll>
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading job scope...
        </div>
      </FormPageLayout>
    );
  }

  if (mode === 'edit' && !changeOrder) {
    return (
      <FormPageLayout title="Change order details" onBack={navigateBack} noscroll>
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Change order not found.
          </div>
        </div>
      </FormPageLayout>
    );
  }

  return (
    <>
      <FormPageLayout
        title={pageTitle}
        subtitle={headerSubtitle}
        onBack={handleExit}
        noscroll
        actions={
          isEditable ? (
            <>
              <Button type="button" variant="outline" size="sm" onClick={handleExit}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canSaveDraft}
                className={canSaveDraft ? 'border-foreground/40 text-foreground' : undefined}
                onClick={() => void save('draft')}
              >
                {(create.isPending || update.isPending) && (
                  <Loader2 className="size-3.5 animate-spin" />
                )}
                Save draft
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canSendForApproval}
                className={
                  canSendForApproval
                    ? undefined
                    : 'bg-muted text-muted-foreground hover:bg-muted disabled:opacity-100'
                }
                onClick={() => void save('send')}
              >
                {(create.isPending || update.isPending || send.isPending) && (
                  <Loader2 className="size-3.5 animate-spin" />
                )}
                {mode === 'edit' ? 'Send for approval' : 'Create & send for approval'}
              </Button>
            </>
          ) : null
        }
      >
        <div className="h-full overflow-y-auto">
          <div className="mx-auto grid max-w-[1120px] gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(0,760px)_300px]">
            <div className="space-y-4">
              <SectionCard className="border-l-4 border-l-foreground/70">
                <div className="space-y-2">
                  <Label
                    htmlFor="co-reason"
                    className="text-xs font-semibold uppercase tracking-wide"
                  >
                    Reason <span className="text-destructive">· Required</span>
                  </Label>
                  <Textarea
                    id="co-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Describe why the job scope is changing"
                    className="min-h-20 text-base"
                    disabled={!isEditable}
                  />
                  <p className="text-sm text-muted-foreground">
                    This is what the customer sees when approving the change.
                  </p>
                </div>
              </SectionCard>

              <SectionCard title="Scope changes">
                <div className="mt-4 space-y-6">
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Current job items
                    </p>
                    {currentItems.length === 0 ? (
                      <p className="rounded-md bg-muted/50 px-3 py-4 text-sm text-muted-foreground">
                        No current job items.
                      </p>
                    ) : (
                      <div className="divide-y rounded-md border">
                        {currentItems.map((item, index) => {
                          const itemId = lineItemId(item, index);
                          const removed = removedIds.has(itemId);
                          const modifyOpen = modifyOpenIds.has(itemId);
                          const draft = modifyDrafts[itemId] ?? modificationDraftFor(item);
                          const appliedModification = modifiedItems.find(
                            (entry) => entry.itemId === itemId,
                          );
                          const modified = Boolean(appliedModification);
                          const originalQuantity = lineItemQuantity(item);
                          const originalTotal = lineItemSubtotal(item);

                          return (
                            <div key={itemId} className="p-3">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <p
                                      className={cn(
                                        'truncate text-sm font-semibold text-foreground',
                                        removed && 'text-muted-foreground line-through',
                                      )}
                                    >
                                      {lineItemName(item)}
                                    </p>
                                    <span className="text-sm text-muted-foreground">
                                      {appliedModification && !removed ? (
                                        <>
                                          {formatItemQuantity(item, originalQuantity)}
                                          <ArrowRight className="mx-1 inline size-3" />
                                          {formatQuantityForBilling(
                                            appliedModification.nextPriceStrategy,
                                            appliedModification.nextQuantity,
                                          )}
                                        </>
                                      ) : (
                                        formatItemQuantity(item, originalQuantity)
                                      )}
                                    </span>
                                    <span className="text-sm font-medium tabular-nums text-muted-foreground">
                                      {appliedModification && !removed ? (
                                        <>
                                          <span className="line-through">
                                            {formatCurrency(originalTotal)}
                                          </span>
                                          <ArrowRight className="mx-1 inline size-3" />
                                          <span className="font-semibold text-foreground">
                                            {formatCurrency(appliedModification.newTotal)}
                                          </span>
                                        </>
                                      ) : (
                                        formatCurrency(originalTotal)
                                      )}
                                    </span>
                                    {removed ? <ChangeTag tone="danger">Removing</ChangeTag> : null}
                                    {modified && !removed ? (
                                      <ChangeTag tone="warning">Modifying</ChangeTag>
                                    ) : null}
                                  </div>
                                </div>
                                {isEditable ? (
                                  <div className="flex shrink-0 items-center gap-2">
                                    {removed ? (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => toggleRemove(itemId)}
                                      >
                                        <Undo2 className="size-4" />
                                        Undo
                                      </Button>
                                    ) : (
                                      <>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => toggleModify(item, itemId)}
                                        >
                                          <Pencil className="size-4" />
                                          Edit
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="text-destructive hover:text-destructive"
                                          onClick={() => toggleRemove(itemId)}
                                        >
                                          <Trash2 className="size-4" />
                                          Remove
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                ) : null}
                              </div>

                              {modifyOpen && !removed ? (
                                <LineItemInlineEditor
                                  item={item}
                                  draft={draft}
                                  onChange={(patch) => updateModifyDraft(itemId, patch)}
                                  onCancel={() => cancelModify(itemId)}
                                  onApply={() => applyModifyDraft(item, itemId)}
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Added items
                    </p>
                    {addedItems.length > 0 ? (
                      <div className="divide-y rounded-md border">
                        {addedItems.map((item) => {
                          // Row state keys on line identity: two configurations
                          // of one configurable item are separate rows, so the
                          // bare catalog id would open/edit/remove both.
                          const rowKey = lineIdentityKey(item);
                          const itemOpen = addedOpenIds.has(rowKey);
                          const isCustomAdded = customAddedIds.has(rowKey);
                          const draft =
                            addedDrafts[rowKey] ?? modificationDraftFor(asJobLineItem(item));

                          return (
                            <div key={rowKey} className="p-3">
                              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_80px_120px_auto] sm:items-center">
                                <div className="min-w-0">
                                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <p className="truncate text-sm font-semibold text-foreground">
                                      {item.name}
                                    </p>
                                    <ChangeTag tone="success">New</ChangeTag>
                                  </div>
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    {item.type} · {item.code || 'Custom item'}
                                  </p>
                                </div>
                                <span className="text-sm font-medium tabular-nums text-muted-foreground">
                                  {formatItemQuantity(asJobLineItem(item), item.quantity)}
                                </span>
                                <span className="text-sm font-semibold tabular-nums text-foreground">
                                  {formatCurrency(lineItemSubtotal(asJobLineItem(item)))}
                                </span>
                                {isEditable ? (
                                  <div className="flex items-center gap-2 sm:justify-end">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => toggleAddedEdit(item)}
                                    >
                                      <Pencil className="size-4" />
                                      Edit
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="text-destructive hover:text-destructive"
                                      onClick={() => removeAddedItem(rowKey)}
                                    >
                                      <Trash2 className="size-4" />
                                      Remove
                                    </Button>
                                  </div>
                                ) : null}
                              </div>

                              {itemOpen ? (
                                <LineItemInlineEditor
                                  item={asJobLineItem(item)}
                                  draft={draft}
                                  onChange={(patch) => updateAddedDraft(rowKey, patch)}
                                  onCancel={() => cancelAddedEdit(rowKey)}
                                  onApply={() => applyAddedDraft(item)}
                                  showItemFields={isCustomAdded}
                                  hideChangeSummary
                                  billingEditable={isCustomAdded}
                                />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    {isEditable ? (
                      <LineItemPicker
                        onAdd={addPickedItem}
                        excludeIds={excludeIds}
                        placeholder="Add item from pricebook..."
                        emptyMessage="No pricebook items found"
                      />
                    ) : null}
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Customer approval">
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Method
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {APPROVAL_METHODS.map((method) => (
                        <Button
                          key={method}
                          type="button"
                          variant={approvalMethod === method ? 'default' : 'outline'}
                          size="sm"
                          disabled={!isEditable}
                          onClick={() => setApprovalMethod(method)}
                        >
                          {method}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {impact && impact.totalDelta > 0
                      ? `This change increases the total (${formatSignedCurrency(impact.totalDelta)}), so it requires customer approval before taking effect.`
                      : impact || !hasChanges
                        ? 'This change does not increase the total, so approval may be optional depending on your configuration.'
                        : 'Changes that increase the total require customer approval before taking effect.'}
                  </p>
                </div>
              </SectionCard>
            </div>

            <aside className="lg:sticky lg:top-4 lg:self-start">
              <section className="rounded-lg bg-muted/70 p-4 shadow-xs">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Financial impact
                </h2>
                <ChangeOrderImpactSummary
                  impact={impact}
                  currentTotal={currentTotal}
                  pending={impactPending}
                  failed={impactFailed}
                />
              </section>
            </aside>
          </div>
        </div>
      </FormPageLayout>

      <AlertDialog
        open={exitConfirmOpen || blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) closeExitConfirm();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes in this change order. Leaving now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeExitConfirm}>Stay</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                event.preventDefault();
                confirmExit();
              }}
            >
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
